import { action, computed, makeObservable, observable, runInAction } from "mobx";
import {
  ApiClient,
  CollectionName,
  Genome,
  GenomeInList,
  GenomesBulkDownloadPayload,
  GenomesOrderBy,
  PaginatedGenomesQuery,
  RestCallResult,
} from "../api";
import { GenomesPageData } from "../utils/genomes";

const DEFAULT_COLLECTION_NAMES: CollectionName[] = ["human_cells", "animal_cells", "organoids"];
const DEFAULT_ORDER_BY: GenomesOrderBy = "taxon.name";

type GenomeDetailsData = {
  genome: Genome | null;
  loading: boolean;
  error: boolean;
};

function getDefaultListData(): GenomesPageData {
  return {
    error: false,
    genomes: observable.array([], { deep: false }),
    loading: false,
    pagination: { page: 1 },
  };
}

function getDefaultDetailsData(): GenomeDetailsData {
  return {
    genome: null,
    loading: false,
    error: false,
  };
}

export default class CellBioStore {
  public api: ApiClient;

  @observable public collectionName: CollectionName | null = null;
  @observable public hydrated = false;
  @observable public listData: GenomesPageData = getDefaultListData();
  @observable public orderBy: GenomesOrderBy = DEFAULT_ORDER_BY;
  @observable public ascending = true;
  @observable private selectionUuids = observable.set<string>([]);
  @observable private selectAllActivated = false;
  @observable public detailsData: GenomeDetailsData = getDefaultDetailsData();

  constructor(api: ApiClient, ssrData?: CellBioStore) {
    makeObservable(this);
    this.api = api;

    if (ssrData) {
      this.hydrated = ssrData.hydrated;
      this.collectionName = ssrData.collectionName;
      this.orderBy = ssrData.orderBy;
      this.ascending = ssrData.ascending;
      this.listData.genomes = observable.array(ssrData.listData.genomes, { deep: false });
      this.listData.loading = false;
      this.listData.error = ssrData.listData.error;
      this.listData.pagination = { ...ssrData.listData.pagination };

      this.detailsData = ssrData.detailsData;
    }
  }

  @computed
  get urlParams(): string {
    const result = new URLSearchParams();

    const page = this.listData.pagination.page || 1;
    if (page > 1) {
      result.set("page", `${page}`);
    }

    if (this.orderBy !== DEFAULT_ORDER_BY) {
      result.set("orderBy", this.orderBy);
    }
    if (!this.ascending) {
      result.set("ascending", "false");
    }

    return result.toString();
  }

  @action
  serverSideInitialFetch = async (
    collectionName: CollectionName | null,
    query: { [key: string]: string[] | string | undefined }
  ) => {
    const searchParams = new URLSearchParams();
    for (const key of Object.keys(query)) {
      const val = query[key];
      if (!val) {
        continue;
      }
      if (typeof val === "string") {
        searchParams.append(key, val);
      } else {
        val.forEach((x) => searchParams.append(key, x));
      }
    }
    this.updateItselfFromSearchParams(searchParams);
    this.setCollectionName(collectionName);
    await this.fetchList();
    runInAction(() => {
      this.hydrated = true;
    });
  };

  @action
  setCollectionName = (newName: CollectionName | null) => {
    this.collectionName = newName;
  };

  @action
  setOrderBy = (orderBy: GenomesOrderBy) => {
    const prevOrderBy = this.orderBy;
    this.orderBy = orderBy;
    if (prevOrderBy !== orderBy) {
      this.ascending = orderBy !== "created_at";
    } else {
      this.ascending = !this.ascending;
    }
    this.fetchList();
  };

  @action
  updateItselfFromSearchParams = (searchParams: URLSearchParams) => {
    if (searchParams.has("page")) {
      this.listData.pagination.page = parseInt(searchParams.get("page") || "", 10) || 1;
    }
    if (searchParams.has("orderBy")) {
      this.orderBy = searchParams.get("orderBy") as GenomesOrderBy;
    }
    if (searchParams.has("ascending")) {
      this.ascending = searchParams.get("ascending") === "true" ? true : false;
    }
    // TODO: params from search query
  };

  @action
  updateIfNotHydrated = (collectionName: CollectionName | null, searchParams: URLSearchParams) => {
    if (this.hydrated) {
      // Data already updated via SSR. But next time we will need to fetch it.
      // So marking itself as non-hydrated.
      this.hydrated = false;
    } else {
      this.collectionName = collectionName;
      this.updateItselfFromSearchParams(searchParams);
      this.fetchList();
      this.selectAllActivated = false;
      this.selectionUuids.clear();
    }
  };

  @action
  setPage = (newPage: number) => {
    this.listData.pagination.page = newPage;
    this.fetchList();
  };

  @action
  fetchList = async () => {
    const collection_name: CollectionName[] = this.collectionName
      ? [this.collectionName]
      : DEFAULT_COLLECTION_NAMES;
    const payload: PaginatedGenomesQuery = {
      order_by: this.orderBy,
      ascending: this.ascending,
      collection_name,
      search_object: {},
    };
    const page = this.listData.pagination.page || 1;

    this.listData.loading = true;
    const result = await this.api.post<GenomeInList[]>(
      "/genome-portal-api/genomes",
      payload,
      {},
      { page: page }
    );

    runInAction(() => {
      if (result.error !== undefined) {
        this.listData.error = true;
        this.listData.genomes.clear();
      } else {
        this.listData.error = false;
        this.listData.genomes.replace(result.data);
        this.listData.pagination = JSON.parse(result.headers.get("X-Pagination")!);
      }
      this.listData.loading = false;
    });
  };

  @action
  fetchDetails = async (uuid: string) => {
    runInAction(() => {
      this.detailsData.loading = true;
    });
    const result = await this.api.get<Genome>("/genome-portal-api/genomes/{uuid}", { uuid });
    runInAction(() => {
      if (result.error === undefined) {
        this.detailsData.error = false;
        this.detailsData.genome = observable(result.data);
      } else {
        this.detailsData.error = true;
        this.detailsData.genome = null;
      }
      this.detailsData.loading = false;
    });
  };

  @action
  fetchDetailsIfNeeded = async (uuid: string) => {
    if (
      !this.detailsData.loading &&
      !this.detailsData.error &&
      this.detailsData.genome?.uuid !== uuid
    ) {
      await this.fetchDetails(uuid);
    }
  };

  @action
  resetDetailsData = () => {
    this.detailsData.error = false;
    this.detailsData.loading = false;
    this.detailsData.genome = null;
  };

  @action
  toggleSelected = (genomeUuid: string) => {
    if (this.selectionUuids.has(genomeUuid)) {
      this.selectionUuids.delete(genomeUuid);
    } else {
      this.selectionUuids.add(genomeUuid);
    }
  };

  @action
  togglePageSelected = (genomeUuids: string[]) => {
    const isPageSelected = this.isPageSelected(genomeUuids);
    let callback =
      (!this.selectAllActivated && isPageSelected) || (this.selectAllActivated && !isPageSelected)
        ? this.selectionUuids.delete
        : this.selectionUuids.add;

    callback = callback.bind(this.selectionUuids);
    for (const uuid of genomeUuids) {
      callback(uuid);
    }
  };

  @action
  selectAll = () => {
    this.selectionUuids.clear();
    this.selectAllActivated = true;
  };

  @action
  deselectAll = () => {
    this.selectionUuids.clear();
    this.selectAllActivated = false;
  };

  @action
  selectOnly = (uuid: string) => {
    this.deselectAll();
    this.toggleSelected(uuid);
  };

  isItemSelected = (genomeUuid: string) => {
    if (this.selectAllActivated) {
      return !this.selectionUuids.has(genomeUuid);
    } else {
      return this.selectionUuids.has(genomeUuid);
    }
  };

  isPageSelected = (genomeUuids: string[]): boolean => {
    if (genomeUuids.length === 0) {
      return false;
    }
    for (const uuid of genomeUuids) {
      if (!this.isItemSelected(uuid)) {
        return false;
      }
    }
    return true;
  };

  getNumSelected = (): { numSelected: number; numTotal: number } => {
    const numTotal = this.listData.pagination.total ?? this.listData.genomes.length;
    const selectedSize = this.selectionUuids.size;
    if (this.selectAllActivated) {
      return { numSelected: numTotal - selectedSize, numTotal };
    }
    return { numSelected: selectedSize, numTotal };
  };

  initBulkDownload = async (
    _opts: Record<string, never>,
    download_types: GenomesBulkDownloadPayload["download_types"]
  ): Promise<RestCallResult<unknown>> => {
    // TODO: add filters when filters are implemented
    const bulkPayload = {
      download_types: download_types,
      genome_uuids: [...this.selectionUuids.values()],
      query_type: !this.selectAllActivated ? "inclusion" : "exclusion",
      collection_name: this.collectionName,
    };
    return await this.api.post<unknown>(
      "/genome-portal-api/genomes/bulk-file-download",
      bulkPayload
    );
  };
}
